/* rs-mobile-nav — dynamic-pane slide navigation for the mobile drawer. */
(() => {
  'use strict';

  const ARROW_SVG = '<svg class="rs-mobile-nav__arrow-icon" aria-hidden="true" focusable="false" width="6" height="10" viewBox="0 0 6 10" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M1 1l4 4-4 4"/></svg>';

  class RsMobileNav extends HTMLElement {
    connectedCallback() {
      this._sectionId = this.dataset.section;
      const dataEl = this.querySelector(`#rs-mobile-nav-data-${this._sectionId}`);
      if (!dataEl) return;
      try { this._data = JSON.parse(dataEl.textContent); } catch { return; }

      this._l1Pane  = this.querySelector('[data-nav-pane="1"]');
      this._l2Pane  = this.querySelector('[data-nav-pane="2"]');
      this._l1List  = this.querySelector('[data-nav-list="1"]');
      this._l2List  = this.querySelector('[data-nav-list="2"]');
      this._l2Back  = this.querySelector('[data-nav-back="2"]');
      this._l2Title = this.querySelector('[data-nav-title="2"]');
      this._backIcon = this._l2Back?.firstElementChild?.outerHTML || '';

      this._level     = 1;
      this._l2Item    = null;
      this._paneStack = [];

      this._l2Back?.addEventListener('click', () => this._backToL1());
      this._watchDrawer();
      this._updateHeight();
      window.addEventListener('resize', () => this._updateHeight(), { passive: true });
      this._renderL1();
    }

    disconnectedCallback() {
      this._observer?.disconnect();
    }

    _watchDrawer() {
      const drawer = this.closest('[data-drawer]');
      if (!drawer) return;
      this._observer = new MutationObserver(() => {
        if (!drawer.classList.contains('is-open')) this._reset();
      });
      this._observer.observe(drawer, { attributes: true, attributeFilter: ['class'] });
    }

    _updateHeight() {
      const drawerHead = this.closest('.drawer__inner')?.querySelector('.drawer__head');
      this.style.setProperty('--rs-nav-height', `calc(100dvh - ${drawerHead ? drawerHead.offsetHeight : 60}px)`);
    }

    _renderL1() {
      const frag = document.createDocumentFragment();
      (this._data.items || []).forEach(item => frag.appendChild(this._buildRow(item, 1)));
      this._l1List.replaceChildren(frag);
    }

    _goL2(item) {
      this._l2Item = item;
      this._clearStack();

      this._l2Title.textContent = item.title;
      const frag = document.createDocumentFragment();
      (item.children || []).forEach(child => frag.appendChild(this._buildRow(child, 2)));
      this._l2List.replaceChildren(frag);
      this._l2List.scrollTop = 0;

      this._l1Pane.classList.add('is-pushed');
      this._l2Pane.classList.add('is-active');
      this._l2Pane.removeAttribute('aria-hidden');
      this._level = 2;
    }

    _backToL1() {
      this._clearStack();
      this._l1Pane.classList.remove('is-pushed');
      this._l2Pane.classList.remove('is-active', 'is-pushed');
      this._l2Pane.setAttribute('aria-hidden', 'true');
      this._l2Item = null;
      this._level  = 1;
    }

    _goL3(item) {
      const prevPane = this._paneStack.at(-1) ?? this._l2Pane;
      prevPane.classList.add('is-pushed');

      const pane = this._buildDeepPane(item);
      this.appendChild(pane);
      this._paneStack.push(pane);
      this._level = 3;

      // rAF lets the browser register the element at translateX(100%) before the transition fires
      requestAnimationFrame(() => pane.classList.add('is-active'));
    }

    _backDeep() {
      const pane     = this._paneStack.pop();
      const prevPane = this._paneStack.at(-1) ?? this._l2Pane;
      prevPane.classList.remove('is-pushed');

      pane.classList.remove('is-active');
      pane.addEventListener('transitionend', () => pane.remove(), { once: true });

      if (!this._paneStack.length) this._level = 2;
    }

    _buildDeepPane(item) {
      const backLabel = this._paneStack.at(-1)?._navItem.title ?? (this._l2Title?.textContent || '');

      const pane = document.createElement('div');
      pane.className = 'rs-mobile-nav__pane rs-mobile-nav__pane--deep';
      pane._navItem = item;
      pane.innerHTML = `
        <div class="rs-mobile-nav__pane-bar">
          <button type="button" class="rs-mobile-nav__back">
            ${this._backIcon}<span>${this._esc(backLabel)}</span>
          </button>
          <span class="rs-mobile-nav__pane-title">${this._esc(item.title)}</span>
        </div>
        <div class="rs-mobile-nav__list" data-scroll-lock-scrollable></div>
      `;

      pane.querySelector('.rs-mobile-nav__back')
        .addEventListener('click', () => this._backDeep());

      const list = pane.querySelector('.rs-mobile-nav__list');
      const frag = document.createDocumentFragment();
      (item.children || []).forEach(child => frag.appendChild(this._buildRow(child, 3)));
      list.replaceChildren(frag);

      return pane;
    }

    _clearStack() {
      this._paneStack.forEach(pane => pane.remove());
      this._paneStack = [];
    }

    _reset() {
      this._clearStack();
      this._l1Pane.classList.remove('is-pushed');
      this._l2Pane.classList.remove('is-active', 'is-pushed');
      this._l2Pane.setAttribute('aria-hidden', 'true');
      this._l2Item = null;
      this._level  = 1;
    }

    _buildRow(item, level) {
      const row = document.createElement('div');
      row.className = 'rs-mobile-nav__row';

      if (item.isHeading) {
        const label = document.createElement('div');
        label.className = 'rs-mobile-nav__section-label';
        label.textContent = item.title;
        row.appendChild(label);
        return row;
      }

      const hasChildren = Array.isArray(item.children) && item.children.length > 0;

      if (hasChildren) {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'rs-mobile-nav__item rs-mobile-nav__item--expandable';
        btn.innerHTML = `<span class="rs-mobile-nav__item-text">${this._esc(item.title)}</span><span class="rs-mobile-nav__arrow" aria-hidden="true">${ARROW_SVG}</span>`;
        if (item.highlight) btn.querySelector('.rs-mobile-nav__item-text').style.color = item.highlight;
        btn.addEventListener('click', () => level === 1 ? this._goL2(item) : this._goL3(item));
        row.appendChild(btn);
      } else {
        const link = document.createElement('a');
        link.href = item.url || '#';
        link.className = 'rs-mobile-nav__item rs-mobile-nav__item--link';
        link.innerHTML = `<span class="rs-mobile-nav__item-text">${this._esc(item.title)}</span>`;
        if (item.highlight) link.querySelector('.rs-mobile-nav__item-text').style.color = item.highlight;
        row.appendChild(link);
      }

      return row;
    }

    _esc(str) {
      const div = document.createElement('div');
      div.textContent = str == null ? '' : String(str);
      return div.innerHTML;
    }
  }

  if (!customElements.get('rs-mobile-nav')) {
    customElements.define('rs-mobile-nav', RsMobileNav);
  }
})();
